import {
  WebsiteDeploymentStatus,
  type WebsiteReadResponse,
  type WebsiteUpsertRequest,
} from 'src/api/generated';

/**
 * How long a deployment may stay queued or running before the backend
 * treats it as stuck and lets a new publication replace it. Mirrors the
 * thresholds in the backend's `WebsitePublishService`.
 */
const STALE_DEPLOYMENT_AGE_MS: Partial<
  Record<WebsiteDeploymentStatus, number>
> = {
  [WebsiteDeploymentStatus.QUEUED]: 10 * 60 * 1000,
  [WebsiteDeploymentStatus.RUNNING]: 30 * 60 * 1000,
};

const compareEntryKeys = (
  [leftKey]: [string, unknown],
  [rightKey]: [string, unknown]
): number => {
  if (leftKey === rightKey) {
    return 0;
  }

  return leftKey < rightKey ? -1 : 1;
};

/**
 * Drops empty values and sorts object keys so two configurations that only
 * differ in key order or blank optional fields compare as equal.
 *
 * @param value - JSON-compatible configuration value.
 * @returns The normalized value.
 */
const normalizeComparableValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(normalizeComparableValue);
  }

  if (value && typeof value === 'object') {
    const entries: [string, unknown][] = Object.entries(value);

    return Object.fromEntries(
      entries
        .filter(
          ([, entry]) => entry !== undefined && entry !== null && entry !== ''
        )
        .sort(compareEntryKeys)
        .map(([key, entry]) => [key, normalizeComparableValue(entry)])
    );
  }

  return value;
};

const serializeComparableValue = (value: unknown): string =>
  JSON.stringify(normalizeComparableValue(value));

/**
 * Checks whether the editor holds changes that the live site does not show
 * yet.
 *
 * A website that was never published has nothing live to differ from, so it
 * reports no changes; the header shows its draft status instead. The design is
 * compared against the saved selection, because deployments do not expose the
 * design they used.
 *
 * @param form - Website draft currently in the editor.
 * @param website - Current website, when one exists.
 * @returns Whether publishing would change the live site.
 */
export const hasUnpublishedWebsiteChanges = (
  form: WebsiteUpsertRequest,
  website: WebsiteReadResponse | null | undefined
): boolean => {
  if (!website?.publishedConfig) {
    return false;
  }

  if (
    form.templateKey !== website.templateKey ||
    form.templateVersion !== website.templateVersion
  ) {
    return true;
  }

  return (
    serializeComparableValue(form.draftConfig) !==
    serializeComparableValue(website.publishedConfig)
  );
};

/**
 * Checks whether the latest publication is still queued or running. The
 * backend rejects a new publication meanwhile, unless the deployment has
 * been stuck long enough to be replaced, so a stuck one never locks the
 * page.
 *
 * @param website - Current website, with its latest deployment first.
 * @param now - Current time in milliseconds, for tests.
 * @returns Whether a publication is in progress.
 */
export const isWebsiteDeploymentInProgress = (
  website: Pick<WebsiteReadResponse, 'deployments'> | null | undefined,
  now: number = Date.now()
): boolean => {
  const latest = website?.deployments?.[0];
  const staleAfterMs = latest ? STALE_DEPLOYMENT_AGE_MS[latest.status] : null;
  if (!latest || !staleAfterMs) {
    return false;
  }

  return now - new Date(latest.updatedAt).getTime() < staleAfterMs;
};
